import React, { useState } from "react";
import {
  TextInput,
  type TextInputProps,
  type StyleProp,
  type TextStyle,
  type ViewStyle,
  View,
} from "react-native";
import type { LucideIcon } from "lucide-react-native";
import {
  MoonFieldInput,
  MoonFormField,
  colors,
  typography,
} from "@louise/moon-ui";
import { useInputFontSize } from "@louise/moon-ui/utils/inputFontSize";

type MedicalHistoryTextFieldProps = Omit<TextInputProps, "style"> & {
  label?: string;
  hint?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  loading?: boolean;
  icon?: LucideIcon;
  /** Use when the label is owned by an outer MoonFormField. */
  labelIcon?: LucideIcon;
  startVisual?: React.ReactNode;
  inputTextStyle?: StyleProp<TextStyle>;
  style?: ViewStyle;
  className?: string;
  testID?: string;
  appearance?: "light" | "dark" | "auto";
};

/**
 * The text field used by Medical History detail forms.
 *
 * Labels are optional only when the caller provides an explicit outer
 * MoonFormField. IconInput requires one so standalone fields cannot regress
 * to placeholder-only context.
 */
export function MedicalHistoryTextField({
  label,
  hint,
  error,
  required,
  disabled,
  loading,
  icon: Icon,
  labelIcon,
  startVisual,
  inputTextStyle,
  style,
  className,
  testID,
  appearance: _appearance,
  multiline,
  editable = true,
  onFocus,
  onBlur,
  ...props
}: MedicalHistoryTextFieldProps) {
  const [focused, setFocused] = useState(false);
  const fontSize = useInputFontSize();
  const visual =
    startVisual ?? (Icon ? <Icon size={16} color={colors.textFaint} /> : null);
  const fieldVisual = label ? null : visual;
  const fieldInput = (
    <MoonFieldInput
      hasError={!!error}
      focused={focused}
      style={style ?? undefined}
    >
      {fieldVisual ? (
        <View style={{ marginRight: 10, paddingTop: multiline ? 2 : 0 }}>
          {fieldVisual}
        </View>
      ) : null}
      <TextInput
        {...props}
        testID={testID}
        editable={editable && !disabled}
        multiline={multiline}
        onFocus={(event) => {
          setFocused(true);
          onFocus?.(event);
        }}
        onBlur={(event) => {
          setFocused(false);
          onBlur?.(event);
        }}
        className={className}
        placeholderTextColor={props.placeholderTextColor ?? colors.textFaint}
        style={[
          {
            flex: 1,
            minHeight: multiline ? 88 : 44,
            paddingVertical: 10,
            paddingHorizontal: 0,
            color: colors.text,
            fontFamily: typography.families.sans,
            fontSize,
            lineHeight: multiline ? 20 : undefined,
            outlineStyle: "none",
            outlineWidth: 0,
          },
          inputTextStyle,
        ]}
      />
    </MoonFieldInput>
  );

  if (!label) return fieldInput;

  return (
    <MoonFormField
      label={label}
      labelIcon={labelIcon ?? (Icon && label ? Icon : undefined)}
      hint={hint}
      error={error}
      required={required}
      disabled={disabled}
      loading={loading}
      testID={testID}
    >
      {fieldInput}
    </MoonFormField>
  );
}
